import { useCallback, useState } from "react";
import {
  AlertTriangle,
  Cloud,
  Container,
  KeyRound,
  Database,
  BookMarked,
  RotateCcw,
  Save,
  Server,
} from "lucide-react";

import {
  PROVIDER_CATEGORIES,
  PROVIDER_META,
  providerHealth,
  providersByCategory,
  useClearGlobalCredentials,
  useCloudRegions,
  useProviders,
  useProvidersStatus,
  useUpdateGlobalEntry,
  type ProviderCategory,
  type ProviderEntry,
  type ProviderEntryPatch,
  type ProviderHealth,
  type ProviderKind,
} from "@/api/local/providers";
import { Pill } from "@/components/Pill";
import { ProviderConfigForm } from "@/components/ProviderConfigForm";
import { Section } from "@/components/Section";
import { ToggleSwitch } from "@/components/ToggleSwitch";
import { VariantsRegionsCell } from "@/components/VariantsRegionsCell";
import {
  overlayValues,
  useProvidersDraft,
} from "@/components/useProvidersDraft";

/**
 * Top-level catalog of cloud / model providers, grouped by SkyPilot
 * "shape" (compute clouds, Kubernetes-style targets, object storage,
 * model registries). Each row is a provider with an enabled toggle, a
 * combined configuration/credential indicator, and a "Configure"
 * button that opens the generic provider form for that kind.
 *
 * Edits to enabled / regions / variants are buffered per-Section in a
 * local `useProvidersDraft` and persisted in one shot when the
 * operator clicks **save** in that section's header — matching the
 * Service / Pool Providers pages.
 *
 * Layout is intentionally compact (4 columns) so it stays usable when
 * the workloads SplitPane leaves only a narrow right pane.
 */
export function GlobalProviders() {
  const doc = useProviders();
  const { isLoading } = useProvidersStatus();
  const updateGlobalEntry = useUpdateGlobalEntry();
  const clearGlobalCredentials = useClearGlobalCredentials();
  const [editing, setEditing] = useState<ProviderKind | null>(null);

  const commit = useCallback(
    async (kind: ProviderKind, patch: ProviderEntryPatch) => {
      await updateGlobalEntry(kind, patch);
    },
    [updateGlobalEntry],
  );

  return (
    <div className="stack" style={{ display: "grid", gap: 16 }}>
      {PROVIDER_CATEGORIES.map((cat) => (
        <CategoryTable
          key={cat.category}
          category={cat.category}
          title={cat.title}
          blurb={cat.blurb}
          kinds={providersByCategory(cat.category)}
          entries={doc.global.entries}
          loading={isLoading}
          commit={commit}
          onConfigure={(k) => setEditing(k)}
        />
      ))}

      {editing && (
        <ProviderConfigForm
          open={true}
          onClose={() => setEditing(null)}
          meta={PROVIDER_META[editing]}
          scopeLabel="Global catalog"
          entry={doc.global.entries[editing]}
          onSave={(patch) => updateGlobalEntry(editing, patch)}
          // The form forwards its "propagate to running services"
          // checkbox state via the optional `opts` argument; spread it
          // into the hook so `?propagate=true` makes it onto the
          // POST /providers/{id}/rotate request when (and only when)
          // the operator opts in.
          onClear={(opts) => clearGlobalCredentials(editing, opts)}
        />
      )}
    </div>
  );
}

const CATEGORY_ICON: Record<ProviderCategory, JSX.Element> = {
  compute: <Cloud />,
  kubernetes: <Server />,
  storage: <Database />,
  registry: <BookMarked />,
  container_registry: <Container />,
};

interface CategoryTableProps {
  category: ProviderCategory;
  title: string;
  blurb: string;
  kinds: ProviderKind[];
  entries: Partial<Record<ProviderKind, ProviderEntry>>;
  /**
   * True while the very first `/providers` fetch is still in flight.
   * Forwarded to each row so it can render a "loading…" pill instead
   * of the misleading "not configured" default.
   */
  loading: boolean;
  /**
   * Page-level write adapter. The Section's draft buffer flushes
   * pending edits through this when the operator hits **save**.
   */
  commit: (kind: ProviderKind, patch: ProviderEntryPatch) => Promise<void>;
  onConfigure: (kind: ProviderKind) => void;
}

function CategoryTable({
  category,
  title,
  blurb,
  kinds,
  entries,
  loading,
  commit,
  onConfigure,
}: CategoryTableProps) {
  // One draft buffer per Section so the save / discard buttons in the
  // header only flush that section's pending edits — keeps an in-flight
  // RunPod region tweak from being rolled by clicking save under the
  // Storage table, and matches the visual scope of each save button.
  const draft = useProvidersDraft(commit);

  const enabled = kinds.filter((k) => {
    const d = draft.drafts[k];
    return d?.enabled ?? entries[k]?.enabled;
  }).length;
  // Only surface the Regions column when at least one provider in the
  // category supports regions — keeps the storage/registry tables tidy
  // (kubeconfig-style targets and HF don't have a region concept).
  const showRegions = kinds.some((k) => PROVIDER_META[k].supportsRegions);
  return (
    <Section
      title={title}
      icon={CATEGORY_ICON[category]}
      count={`${enabled} / ${kinds.length}`}
      flush
      actions={<DraftActions draft={draft} />}
    >
      <p className="muted" style={{ padding: "10px 18px 0", margin: 0, fontSize: 12.5 }}>
        {blurb} Edits are staged locally — click <strong>save</strong> above to
        persist them.
      </p>
      <div className="table-wrap">
        <table className="data provider-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th style={{ width: 90 }}>Enabled</th>
              {showRegions && <th>Regions</th>}
              <th>Credential</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {kinds.map((kind) => {
              const entry = entries[kind];
              const d = draft.drafts[kind];
              const enabledEffective =
                d?.enabled ?? Boolean(entry?.enabled);
              // Overlay any in-progress region/variant edits on top of
              // the server-side row so the chips reflect the *intended*
              // state while the row is dirty (otherwise the operator
              // adds a region, sees it appear, then sees it pop back to
              // the server view on the next render).
              const variantSourceEntry: ProviderEntry | undefined = entry
                ? { ...entry, values: overlayValues(entry, d) }
                : d?.values
                  ? {
                      id: null,
                      enabled: enabledEffective,
                      values: { ...d.values },
                      secret_keys: [],
                      key_last4: null,
                    }
                  : undefined;
              return (
                <ProviderRow
                  key={kind}
                  kind={kind}
                  entry={entry}
                  variantSourceEntry={variantSourceEntry}
                  loading={loading}
                  showRegions={showRegions}
                  enabled={enabledEffective}
                  dirty={d !== undefined}
                  onConfigure={() => onConfigure(kind)}
                  onToggleEnabled={(v) => draft.setEnabled(kind, v)}
                  onChangeVariant={(variantKey, next) =>
                    draft.setVariant(kind, variantKey, next)
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function DraftActions({
  draft,
}: {
  draft: ReturnType<typeof useProvidersDraft>;
}) {
  const { isDirty, saving, error, dirtyKinds, save, discard } = draft;
  return (
    <div className="toolbar" style={{ gap: 8, alignItems: "center" }}>
      {error && (
        <span
          className="flash"
          style={{ padding: "2px 8px", fontSize: 12 }}
          title={error}
        >
          <AlertTriangle
            style={{ width: 12, height: 12, verticalAlign: "-2px", marginRight: 4 }}
          />
          save failed
        </span>
      )}
      {isDirty && (
        <span className="muted" style={{ fontSize: 12 }}>
          {dirtyKinds.length} unsaved row{dirtyKinds.length === 1 ? "" : "s"}
        </span>
      )}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={discard}
        disabled={!isDirty || saving}
        title="Drop pending edits and revert"
      >
        <RotateCcw />
        discard
      </button>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => void save()}
        disabled={!isDirty || saving}
        title={isDirty ? "Persist every pending row edit" : "no pending changes"}
      >
        <Save />
        {saving ? "saving…" : "save"}
      </button>
    </div>
  );
}

interface ProviderRowProps {
  kind: ProviderKind;
  entry: ProviderEntry | undefined;
  /**
   * Entry shape with the in-progress draft overlay merged in. Feeds
   * into `VariantsRegionsCell` so chips reflect the intended state
   * while the row is dirty.
   */
  variantSourceEntry: ProviderEntry | undefined;
  /**
   * True while the very first `/providers` fetch is still in flight.
   * In that window `entry` is always undefined for every row (the
   * doc is `EMPTY_DOC`), so without this signal we'd render every
   * provider as "not configured" — exactly the misleading state
   * operators reported confusing them right after the page opens.
   */
  loading: boolean;
  showRegions: boolean;
  /** Effective enabled state (draft overlay if dirty, else server). */
  enabled: boolean;
  dirty: boolean;
  onConfigure: () => void;
  onToggleEnabled: (next: boolean) => void;
  onChangeVariant: (
    variantKey: string,
    next: { regions: string[]; enabled: boolean },
  ) => void;
}

function ProviderRow({
  kind,
  entry,
  variantSourceEntry,
  loading,
  showRegions,
  enabled,
  dirty,
  onConfigure,
  onToggleEnabled,
  onChangeVariant,
}: ProviderRowProps) {
  const meta = PROVIDER_META[kind];
  // While the initial fetch is in flight `entry` is always undefined
  // (the hook synthesises EMPTY_DOC), which would otherwise resolve
  // to "unconfigured". We override to a dedicated "loading" signal so
  // the row's pill / toggle / configure button all reflect "we don't
  // know yet" rather than "definitely not configured".
  const health: ProviderHealth | "loading" = loading && !entry
    ? "loading"
    : providerHealth(meta, entry);
  const last4 = entry?.key_last4;
  const regionSuggestions = useCloudRegions(
    meta.supportsRegions ? kind : undefined,
  );

  // A provider with a known blocking issue is greyed out and carries an
  // explicit warning so nobody burns time configuring something that
  // cannot work end-to-end right now (see ProviderMeta.knownIssue).
  const blocked = Boolean(meta.knownIssue);

  return (
    <tr
      className={dirty ? "row--dirty" : undefined}
      style={blocked ? { opacity: 0.55 } : undefined}
    >
      <td>
        <div className="provider-cell">
          <strong className="provider-cell__label">
            {meta.label}
            {blocked && (
              <span style={{ marginLeft: 6 }}>
                <Pill tone="warn" title={meta.knownIssue}>
                  unusable
                </Pill>
              </span>
            )}
          </strong>
          <div className="provider-cell__hint muted">{meta.hint}</div>
          {blocked && (
            <div
              className="provider-cell__note"
              style={{ fontSize: 11, marginTop: 2, color: "var(--warn, #b45309)", maxWidth: 340 }}
            >
              {meta.knownIssue}
            </div>
          )}
          {entry?.note && (
            <div className="provider-cell__note muted">note: {entry.note}</div>
          )}
          {dirty && (
            <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>
              unsaved
            </div>
          )}
        </div>
      </td>
      <td>
        <ToggleSwitch
          checked={enabled}
          onChange={onToggleEnabled}
          disabled={health === "loading"}
          ariaLabel={`Enable ${meta.label}`}
        />
      </td>
      {showRegions && (
        <td>
          {meta.supportsRegions ? (
            <VariantsRegionsCell
              kind={kind}
              entry={variantSourceEntry}
              suggestions={regionSuggestions}
              readOnly={health === "loading"}
              onChangeVariant={onChangeVariant}
            />
          ) : (
            <span className="muted" style={{ fontSize: 11.5 }}>
              n/a
            </span>
          )}
        </td>
      )}
      <td>
        <div className="toolbar" style={{ gap: 6, flexWrap: "wrap" }}>
          <HealthPill health={health} />
          {last4 && <Pill tone="ok">…{last4}</Pill>}
        </div>
        {entry?.sky_check_message && health === "failed" && (
          // Disclose the captured `sky check` stderr only on failures.
          // On success there's nothing useful to read; on pending the
          // string is empty; on skipped/unconfigured the persisted
          // message (if any) is administrative ("credentials cleared")
          // and not worth surfacing in the row.
          <details
            className="muted"
            style={{ fontSize: 11, marginTop: 4, maxWidth: 320 }}
          >
            <summary style={{ cursor: "pointer" }}>show error</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                marginTop: 4,
                fontSize: 11,
              }}
            >
              {entry.sky_check_message}
            </pre>
          </details>
        )}
        {entry?.sky_check_at && health !== "unconfigured" && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            checked {new Date(entry.sky_check_at).toLocaleString()}
          </div>
        )}
        {entry?.rotated_at && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            rotated {new Date(entry.rotated_at).toLocaleString()}
          </div>
        )}
      </td>
      <td className="actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={onConfigure}
          disabled={health === "loading"}
        >
          <KeyRound />
          configure
        </button>
      </td>
    </tr>
  );
}

/**
 * Render the composite health (DB completeness + last `sky check`
 * result) as a single tone-coloured pill.
 *
 * We deliberately do NOT use a green "ok" pill for the `disabled`
 * state — the credentials are present but SkyPilot isn't using them,
 * which is genuinely different from the all-clear "ready" state. The
 * neutral tone makes that distinction visible at a glance.
 *
 * The extra `"loading"` variant is set by the row component while the
 * very first `/providers` fetch is in flight. Without it the empty
 * `EMPTY_DOC` placeholder would resolve to `"unconfigured"` and the
 * operator would briefly see every provider as "not configured" — the
 * exact false alarm this pill is supposed to short-circuit.
 */
function HealthPill({ health }: { health: ProviderHealth | "loading" }) {
  switch (health) {
    case "loading":
      return (
        <Pill tone="info" pulse title="loading provider state…">
          loading…
        </Pill>
      );
    case "ok":
      return <Pill tone="ok">ready</Pill>;
    case "failed":
      return (
        <Pill tone="danger" title="sky check returned non-zero">
          auth failed
        </Pill>
      );
    case "pending":
      return (
        <Pill tone="info" pulse title="sky check is running">
          checking…
        </Pill>
      );
    case "skipped":
      return (
        <Pill
          tone="warn"
          title="The controller does not yet ship a credential writer for this cloud; the row is saved but SkyPilot can't see it."
        >
          not synced
        </Pill>
      );
    case "disabled":
      return <Pill tone="neutral">configured (off)</Pill>;
    case "unconfigured":
    default:
      return <Pill tone="warn">not configured</Pill>;
  }
}
